import { useEffect, useRef, useState } from 'react';

export type ActivityDisplayStrength = 'strong' | 'weak';
export type ActivityDisplayPriority = 'transition' | 'work' | 'high';

export const MINIMUM_ACTIVITY_VISIBLE_MS = 300;
export const WEAK_ACTIVITY_VISIBLE_DELAY_MS = 1000;
export const DEFAULT_WORK_ACTIVITY_DISPLAY_HOLD_MS = 5000;

export function normalizeActivityDisplayStrength(
  value: string | null | undefined
): ActivityDisplayStrength {
  return value?.trim().toLowerCase() === 'weak' ? 'weak' : 'strong';
}

export function normalizeActivityDisplayPriority(
  value: string | null | undefined
): ActivityDisplayPriority {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'work' || normalized === 'high') {
    return normalized;
  }
  return 'transition';
}

export function useActivityPresentation<T>(
  value: T,
  valueKey: string,
  {
    displayStrength,
    displayPriority,
    displayHoldMs,
    emptyKey,
    emptyValue,
    minimumMs = MINIMUM_ACTIVITY_VISIBLE_MS,
    resetKey,
    weakDelayMs = WEAK_ACTIVITY_VISIBLE_DELAY_MS,
    isEmpty,
  }: {
    displayStrength?: string | null;
    displayPriority?: string | null;
    displayHoldMs?: number | null;
    emptyKey: string;
    emptyValue: T;
    minimumMs?: number;
    resetKey?: string | number | null;
    weakDelayMs?: number;
    isEmpty: (value: T) => boolean;
  }
): T {
  const initialStrength = normalizeActivityDisplayStrength(displayStrength);
  const initialPriority = normalizeActivityDisplayPriority(displayPriority);
  const initialHoldMs = normalizeActivityDisplayHoldMs(
    displayHoldMs,
    initialPriority
  );
  const initialCandidate = activityPresentationResetCandidate({
    value,
    valueKey,
    strength: initialStrength,
    priority: initialPriority,
    holdMs: initialHoldMs,
    emptyKey,
    emptyValue,
    isEmpty,
  });
  const initialValue = initialCandidate.value;
  const initialKey = initialCandidate.key;

  const [visibleValue, setVisibleValue] = useState(initialValue);
  const visibleValueRef = useRef(initialValue);
  const visibleKeyRef = useRef(initialKey);
  const visiblePriorityRef = useRef(initialPriority);
  const visibleHoldMsRef = useRef(initialHoldMs);
  const visibleSinceRef = useRef(Date.now());
  const pendingDisplayRef = useRef<ActivityPresentationCandidate<T> | null>(
    null
  );
  const pendingWeakRef = useRef<ActivityPresentationCandidate<T> | null>(null);
  const displayTimerRef = useRef<number | null>(null);
  const weakTimerRef = useRef<number | null>(null);
  const resetKeyRef = useRef(resetKey);

  useEffect(() => {
    return () => {
      clearTimer(displayTimerRef);
      clearTimer(weakTimerRef);
    };
  }, []);

  useEffect(() => {
    const clearDisplayTimer = () => clearTimer(displayTimerRef);
    const clearWeakTimer = () => clearTimer(weakTimerRef);
    const clearPendingDisplay = () => {
      pendingDisplayRef.current = null;
      clearDisplayTimer();
    };

    const applyVisible = (
      nextValue: T,
      nextKey: string,
      nextPriority: ActivityDisplayPriority,
      nextHoldMs: number
    ) => {
      visibleValueRef.current = nextValue;
      visibleKeyRef.current = nextKey;
      visiblePriorityRef.current = nextPriority;
      visibleHoldMsRef.current = nextHoldMs;
      visibleSinceRef.current = Date.now();
      setVisibleValue(nextValue);
    };

    const scheduleDisplay = (
      candidate: ActivityPresentationCandidate<T>,
      delayMs: number
    ) => {
      pendingDisplayRef.current = candidate;
      clearDisplayTimer();
      displayTimerRef.current = window.setTimeout(() => {
        displayTimerRef.current = null;
        const pending = pendingDisplayRef.current;
        if (!pending) {
          return;
        }
        pendingDisplayRef.current = null;
        applyVisible(
          pending.value,
          pending.key,
          pending.priority,
          pending.holdMs
        );
      }, delayMs);
    };

    const queueDisplay = (candidate: ActivityPresentationCandidate<T>) => {
      const {
        value: nextValue,
        key: nextKey,
        priority: nextPriority,
      } = candidate;
      if (nextKey === visibleKeyRef.current) {
        clearPendingDisplay();
        visiblePriorityRef.current = nextPriority;
        visibleHoldMsRef.current = candidate.holdMs;
        return;
      }

      const elapsedMs = Date.now() - visibleSinceRef.current;
      const minimumRemainingMs = Math.max(0, minimumMs - elapsedMs);
      const holdRemainingMs = visibleHoldRemainingMs(
        visiblePriorityRef.current,
        visibleHoldMsRef.current,
        nextPriority,
        elapsedMs
      );
      const replacementDelayMs = Math.max(minimumRemainingMs, holdRemainingMs);
      if (isEmpty(nextValue)) {
        clearPendingDisplay();
        pendingWeakRef.current = null;
        clearWeakTimer();
        applyVisible(nextValue, nextKey, nextPriority, candidate.holdMs);
        return;
      }
      if (isEmpty(visibleValueRef.current) || replacementDelayMs <= 0) {
        clearPendingDisplay();
        applyVisible(nextValue, nextKey, nextPriority, candidate.holdMs);
        return;
      }

      scheduleDisplay(candidate, replacementDelayMs);
    };

    const priority = normalizeActivityDisplayPriority(displayPriority);
    const holdMs = normalizeActivityDisplayHoldMs(displayHoldMs, priority);
    const candidate = { value, key: valueKey, priority, holdMs };
    const resetCandidate = activityPresentationResetCandidate({
      value,
      valueKey,
      strength: normalizeActivityDisplayStrength(displayStrength),
      priority,
      holdMs,
      emptyKey,
      emptyValue,
      isEmpty,
    });
    const resetChanged = resetKeyRef.current !== resetKey;
    if (resetChanged) {
      resetKeyRef.current = resetKey;
      clearPendingDisplay();
      pendingWeakRef.current = null;
      clearWeakTimer();
      applyVisible(
        resetCandidate.value,
        resetCandidate.key,
        resetCandidate.priority,
        resetCandidate.holdMs
      );
      return;
    }

    if (valueKey === visibleKeyRef.current) {
      clearPendingDisplay();
      pendingWeakRef.current = null;
      clearWeakTimer();
      return;
    }

    const strength = normalizeActivityDisplayStrength(displayStrength);
    if (strength === 'weak' && !isEmpty(value)) {
      if (pendingDisplayRef.current?.key === valueKey) {
        return;
      }
      clearPendingDisplay();
      if (pendingWeakRef.current?.key === valueKey) {
        return;
      }
      pendingWeakRef.current = candidate;
      clearWeakTimer();
      weakTimerRef.current = window.setTimeout(() => {
        weakTimerRef.current = null;
        const pending = pendingWeakRef.current;
        if (!pending) {
          return;
        }
        pendingWeakRef.current = null;
        queueDisplay(pending);
      }, weakDelayMs);
      return;
    }

    pendingWeakRef.current = null;
    clearWeakTimer();
    queueDisplay(candidate);
  }, [
    displayHoldMs,
    displayPriority,
    displayStrength,
    emptyKey,
    emptyValue,
    isEmpty,
    minimumMs,
    resetKey,
    value,
    valueKey,
    weakDelayMs,
  ]);

  if (resetKeyRef.current !== resetKey) {
    const priority = normalizeActivityDisplayPriority(displayPriority);
    const resetCandidate = activityPresentationResetCandidate({
      value,
      valueKey,
      strength: normalizeActivityDisplayStrength(displayStrength),
      priority,
      holdMs: normalizeActivityDisplayHoldMs(displayHoldMs, priority),
      emptyKey,
      emptyValue,
      isEmpty,
    });
    return resetCandidate.value;
  }

  return visibleValue;
}

interface ActivityPresentationCandidate<T> {
  value: T;
  key: string;
  priority: ActivityDisplayPriority;
  holdMs: number;
}

function activityPresentationResetCandidate<T>({
  value,
  valueKey,
  strength,
  priority,
  holdMs,
  emptyKey,
  emptyValue,
  isEmpty,
}: {
  value: T;
  valueKey: string;
  strength: ActivityDisplayStrength;
  priority: ActivityDisplayPriority;
  holdMs: number;
  emptyKey: string;
  emptyValue: T;
  isEmpty: (value: T) => boolean;
}): ActivityPresentationCandidate<T> {
  if (strength === 'weak' && !isEmpty(value)) {
    return {
      value: emptyValue,
      key: emptyKey,
      priority: 'transition',
      holdMs: 0,
    };
  }
  return {
    value,
    key: valueKey,
    priority,
    holdMs,
  };
}

function normalizeActivityDisplayHoldMs(
  value: number | null | undefined,
  priority: ActivityDisplayPriority
) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return priority === 'work' ? DEFAULT_WORK_ACTIVITY_DISPLAY_HOLD_MS : 0;
  }
  return Math.floor(value);
}

function visibleHoldRemainingMs(
  visiblePriority: ActivityDisplayPriority,
  visibleHoldMs: number,
  nextPriority: ActivityDisplayPriority,
  elapsedMs: number
) {
  if (
    visiblePriority !== 'work' ||
    visibleHoldMs <= 0 ||
    nextPriority !== 'transition'
  ) {
    return 0;
  }
  return Math.max(0, visibleHoldMs - elapsedMs);
}

function clearTimer(timerRef: { current: number | null }) {
  if (timerRef.current !== null) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}
